import { lazy, type ComponentType } from 'react';

/**
 * `lazy` wrapper que maneja el caso "chunk antiguo 404 después de deploy".
 *
 * Síntomas del problema:
 *   - "'text/html' is not a valid JavaScript MIME type"
 *   - "Failed to fetch dynamically imported module"
 *   - "Loading chunk X failed"
 *
 * Causa: el usuario tiene un HTML viejo en cache que referencia chunks con hash
 * que ya no existen en Vercel. Al intentar cargarlos, Vercel responde con
 * index.html (SPA fallback) → el navegador lo rechaza.
 *
 * Solución: detectar el error, forzar un reload (con flag en sessionStorage
 * para evitar loop infinito si el error es real).
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const RELOAD_FLAG = 'az_chunk_reload_attempted';
    try {
      return await factory();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const isChunkError =
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Loading chunk \d+ failed/i.test(message) ||
        /'text\/html' is not a valid JavaScript MIME type/i.test(message) ||
        /error loading dynamically imported module/i.test(message);

      if (isChunkError && !sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        // Limpiar caches del service worker y forzar reload "hard"
        try {
          if ('caches' in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
          if ('serviceWorker' in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
        } catch {
          // ignorar, de todas formas recargamos
        }
        window.location.reload();
        // Nunca resuelve porque la página se recarga
        return new Promise<{ default: T }>(() => {});
      }

      // Si ya intentamos reload o no es chunk error, dejar que ErrorBoundary lo tome
      throw err;
    }
  });
}

/**
 * Limpia el flag de reload cuando la app carga exitosamente, para que un
 * futuro deploy pueda volver a intentar auto-recuperarse.
 */
export function clearChunkReloadFlag() {
  try {
    sessionStorage.removeItem('az_chunk_reload_attempted');
  } catch {
    // ignorar
  }
}
